import { Link } from "react-router-dom";
import { CheckCircle2, Instagram, Linkedin, Facebook, Star } from "lucide-react";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { COMPANY, SOCIALS } from "@/data/company";

const COMPANY_CRN = COMPANY.crn;

export const Footer = () => {
  const { openCookiePreferences } = useCookieConsent();
  return (
  <footer className="bg-pula-ink text-pula-bg mt-24">
    <div className="max-w-7xl mx-auto px-4 py-16 grid gap-10 md:grid-cols-4">
      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <span className="w-9 h-9 rounded-xl bg-pula-green grid place-items-center text-white font-heading font-extrabold text-lg">P</span>
          <span className="font-heading font-extrabold text-xl">Pula</span>
        </div>
        <p className="font-heading text-lg max-w-md leading-snug">Care work in the UK — free to apply, with free prep to help you get ready.</p>
        <p className="text-sm text-pula-bg/60 mt-2 font-body">Built with love for the Southern African diaspora. Part of the Mavula Investment family.</p>
        <Link to="/care-jobs-uk" data-testid="footer-care-cta" className="inline-flex items-center gap-1.5 mt-4 rounded-full bg-pula-green hover:bg-pula-greenhover text-white text-sm font-semibold px-4 py-2 transition-colors">Browse care jobs</Link>
      </div>

      <div>
        <h4 className="overline text-pula-green mb-4">Care Jobs UK</h4>
        <ul className="space-y-2 text-sm font-body">
          <li><Link to="/care-jobs-uk" data-testid="footer-care-jobs" className="text-pula-bg/80 hover:text-white transition-colors">Browse care jobs</Link></li>
          <li><Link to="/care-eligibility" data-testid="footer-care-eligibility" className="text-pula-bg/80 hover:text-white transition-colors">Check if you're ready</Link></li>
          <li><Link to="/care-training" data-testid="footer-care-training" className="text-pula-bg/80 hover:text-white transition-colors">Free self-study prep</Link></li>
          <li><Link to="/jobs/my-applications" data-testid="footer-care-applications" className="text-pula-bg/80 hover:text-white transition-colors">Track my applications</Link></li>
          <li><Link to="/for-employers" data-testid="footer-care-employers" className="text-pula-bg/80 hover:text-white transition-colors">For care employers</Link></li>
        </ul>
      </div>

      <div>
        <h4 className="overline text-pula-green mb-4">Explore Pula</h4>
        <ul className="space-y-2 text-sm font-body">
          <li><Link to="/local" data-testid="footer-local" className="text-pula-bg/80 hover:text-white transition-colors">Pula Local (Botswana)</Link></li>
          <li><Link to="/for-business" data-testid="footer-for-business" className="text-pula-bg/80 hover:text-white transition-colors">List your business — free</Link></li>
          <li><Link to="/market" data-testid="footer-market" className="text-pula-bg/80 hover:text-white transition-colors">Pula Market</Link></li>
          <li><Link to="/properties" data-testid="footer-properties" className="text-pula-bg/80 hover:text-white transition-colors">Homes &amp; Properties</Link></li>
          <li><Link to="/jobs" data-testid="footer-jobs" className="text-pula-bg/80 hover:text-white transition-colors">Pula Jobs</Link></li>
          <li><Link to="/learn" data-testid="footer-learn" className="text-pula-bg/80 hover:text-white transition-colors">Pula Learn</Link></li>
          <li><Link to="/love" data-testid="footer-love" className="text-pula-bg/80 hover:text-white transition-colors">Pula Love</Link></li>
          <li><Link to="/lens" data-testid="footer-lens" className="text-pula-bg/80 hover:text-white transition-colors">Pula Lens — Stories</Link></li>
          <li><Link to="/wallet" data-testid="footer-wallet" className="text-pula-bg/80 hover:text-white transition-colors">Pula Wallet</Link></li>
          <li><Link to="/explore" data-testid="footer-explore-all" className="text-pula-green hover:text-white transition-colors font-medium">See everything →</Link></li>
        </ul>
      </div>

      <div>
        <h4 className="overline text-pula-green mb-4">Company &amp; Trust</h4>
        <ul className="space-y-2 text-sm font-body">
          <li><Link to="/about" data-testid="footer-about" className="text-pula-bg/80 hover:text-white transition-colors">How Pula works</Link></li>
          <li><Link to="/love/safety" data-testid="footer-safety-trust" className="text-white font-semibold hover:text-pula-green transition-colors">🛡️ Safety &amp; Trust hub</Link></li>
          <li><Link to="/whats-real" data-testid="footer-whats-real" className="text-white font-semibold hover:text-pula-green transition-colors">What's real today</Link></li>
          <li><Link to="/how-we-make-money" data-testid="footer-how-we-make-money" className="text-pula-bg/80 hover:text-white transition-colors">How Pula makes money</Link></li>
          <li><Link to="/trust" data-testid="footer-trust" className="text-pula-bg/80 hover:text-white transition-colors">How Pula protects you</Link></li>
          <li><Link to="/account/data" data-testid="footer-my-data" className="text-pula-bg/80 hover:text-white transition-colors">Your data &amp; privacy</Link></li>
          <li><Link to="/privacy" data-testid="footer-privacy" className="text-pula-bg/80 hover:text-white transition-colors">Privacy Policy</Link></li>
          <li><Link to="/terms" data-testid="footer-terms" className="text-pula-bg/80 hover:text-white transition-colors">Terms &amp; Rules</Link></li>
          <li><a href="https://mavula-invest-sa.emergent.host" target="_blank" rel="noopener noreferrer" data-testid="footer-mavula-site" className="text-pula-bg/80 hover:text-white transition-colors inline-flex items-center gap-1">Mavula Investment <span aria-hidden>↗</span></a></li>
          <li><Link to="/contact" data-testid="footer-contact" className="text-pula-bg/80 hover:text-white transition-colors">Contact us</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10" data-testid="footer-founder-note">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <blockquote className="max-w-2xl text-pula-bg/85 font-body italic leading-relaxed">
          “I built this for my daughter, for my people, and for everyone who deserves a fair chance. This is our home — open to all.”
          <span className="block mt-2 not-italic text-sm text-pula-bg/60">— Founder, Mavula Investment</span>
        </blockquote>
      </div>
    </div>
    <div className="border-t border-white/10" data-testid="footer-legal">
      <div className="max-w-4xl mx-auto px-4 py-10 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="text-lg" aria-hidden>🌧️</span>
          <span className="font-heading font-extrabold text-lg text-pula-bg">Pula — Rain of Blessings</span>
        </div>
        <p className="text-sm text-pula-bg/80 font-body">Operated by <strong className="text-pula-bg">{COMPANY.name}</strong></p>
        <p className="text-xs text-pula-bg/60 font-body mt-1">
          A trading name of {COMPANY.legalName} · Registered in {COMPANY.jurisdiction}{COMPANY_CRN ? ` · Company No. ${COMPANY_CRN}` : ""}
        </p>
        <p className="text-xs text-pula-bg/60 font-body mt-1">
          Registered Office: {COMPANY.registeredOffice}
        </p>

        <p className="text-xs text-pula-bg/70 font-body mt-4">
          📧 Contact:{" "}
          <a href="mailto:support@pulalove.com" data-testid="footer-contact-email" className="text-pula-green hover:text-white transition-colors">support@pulalove.com</a>
          {" · "}
          📞{" "}
          <a href="tel:+447307206722" data-testid="footer-phone" className="text-pula-green hover:text-white transition-colors">+44 7307 206722</a>
          {" · "}
          <a href="https://wa.me/447307206722?text=Hi%20Pula%20%F0%9F%91%8B" target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="text-pula-green hover:text-white transition-colors">WhatsApp</a>
        </p>

        {SOCIALS.length > 0 && (
          <div className="flex items-center justify-center gap-3 mt-4" data-testid="footer-socials">
            {SOCIALS.map((s) => {
              const Icon = s.name === "Instagram" ? Instagram : s.name === "LinkedIn" ? Linkedin : s.name === "Facebook" ? Facebook : s.name === "Trustpilot" ? Star : Instagram;
              return (
                <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.name} title={s.handle || s.name}
                  data-testid={`footer-social-${s.name.toLowerCase()}`}
                  className="w-9 h-9 grid place-items-center rounded-full bg-white/10 text-pula-bg hover:bg-pula-green hover:text-white transition-colors">
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-4 text-xs font-body" data-testid="footer-legal-links">
          <Link to="/our-promise" data-testid="legal-our-promise" className="text-pula-bg/80 hover:text-white transition-colors">Our Promise</Link>
          <span className="text-pula-bg/30">·</span>
          <Link to="/privacy" data-testid="legal-privacy" className="text-pula-bg/80 hover:text-white transition-colors">Privacy Policy</Link>
          <span className="text-pula-bg/30">·</span>
          <Link to="/terms" data-testid="legal-terms" className="text-pula-bg/80 hover:text-white transition-colors">Terms of Service</Link>
          <span className="text-pula-bg/30">·</span>
          <button onClick={openCookiePreferences} data-testid="legal-cookies" className="text-pula-bg/80 hover:text-white transition-colors">Cookie Settings</button>
          <span className="text-pula-bg/30">·</span>
          <Link to="/code-of-conduct" data-testid="legal-conduct" className="text-pula-bg/80 hover:text-white transition-colors">Code of Conduct</Link>
        </div>

        <div className="mt-6 max-w-xl mx-auto text-left inline-block" data-testid="footer-important-info">
          <p className="font-heading font-semibold text-pula-bg/80 text-xs mb-2 text-center">🛡️ Important Information</p>
          <ul className="space-y-1.5 text-xs text-pula-bg/70 font-body">
            {[
              "We never sell your personal data",
              "Not a bank, e-money issuer, money-transfer service or immigration authority",
              "Your Pula balance can only be spent inside Pula — it is not cash and cannot be withdrawn or sent as money",
              "Regulated payment services are provided by our licensed partners",
              "All home ownership pathways are subject to legal checks, property valuation and approval",
              "Features marked “Coming Soon” are in development and subject to regulatory confirmation",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-pula-green shrink-0 mt-0.5" /> <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="text-xs text-pula-bg/50 font-body mt-6">© {new Date().getFullYear()} MAVULAINVESTMENT LTD. All rights reserved.</p>
      </div>
    </div>
  </footer>
  );
};
